import { Injectable } from '@angular/core'
import { AppDownloadService } from '@ftr/api-core'
import { TRANSCRIBER_PERMISSIONS, UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { unwrapData } from '@ftr/foundation'
import { AppPaths, OrderPaths } from '@ftr/routing-paths'
import { FeatureFlagState } from '@ftr/ui-feature-flags'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { Observable, combineLatest, map } from 'rxjs'
import { SideNavGroup, SideNavItem, getGlobalAdminNavItem, getHelpSupportNavItem } from '../side-nav-item'

const DEFAULT_NAV_ITEMS = (replaceRealtimeWordingFlagEnabled?: boolean): Record<string, SideNavItem> => ({
  'transcript-jobs': {
    type: 'LINK',
    title: 'Transcript Jobs',
    path: [`/${AppPaths.Jobs}`],
    icon: 'TranscriptJobs',
  } as SideNavItem,
  'upload-and-play': {
    type: 'LINK',
    title: 'Upload & Play',
    path: [`/${AppPaths.Uploads}`],
    icon: 'UploadAndPlay',
  } as SideNavItem,
  'my-orders': {
    type: 'LINK',
    title: 'My Orders',
    path: [`/${AppPaths.Orders}`],
    icon: 'MyOrders',
  } as SideNavItem,
  'shared-recordings': {
    type: 'LINK',
    title: 'Shared Recordings',
    path: [`/${AppPaths.SharedRecordings}`],
    icon: 'SharedRecordings',
  } as SideNavItem,
  'order-audio': {
    type: 'LINK',
    title: 'Audio',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Audio],
    icon: 'AudioOrder',
    tooltipHint: 'Place an Audio Order',
  } as SideNavItem,
  'order-real-time': {
    type: 'LINK',
    title: replaceRealtimeWordingFlagEnabled ? 'Live' : 'RealTime',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.RealTime],
    icon: 'RealtimeOrder',
    tooltipHint: `Place a ${replaceRealtimeWordingFlagEnabled ? 'Live' : 'Realtime'} Order`,
  } as SideNavItem,
  'order-transcript': {
    type: 'LINK',
    title: 'Transcript',
    path: [`/${AppPaths.Orders}`, OrderPaths.ChooseCourt, OrderPaths.Transcript],
    icon: 'TranscriptOrder',
    tooltipHint: 'Place a Transcript Order',
  } as SideNavItem,
  'select-court-system': {
    type: 'LINK',
    title: 'Select a Court System',
    path: ['/court-system'],
    icon: 'Gavel',
  } as SideNavItem,
})

@Injectable({
  providedIn: 'root',
})
export class ExternalUserNavItemsProvider {
  constructor(
    private readonly store: Store,
    private readonly appDownloadService: AppDownloadService,
  ) {}

  provide(): Observable<{ navGroups: SideNavGroup[]; footerItems: SideNavItem[] }> {
    return combineLatest([
      this.store.select(UserState.courtSystems).pipe(unwrapData()),
      this.store
        .select(UserState.hasPermissionInAnyCourtSystem)
        .pipe(map(fn => fn(UserGroupPermissionId.TranscribeRecordings))),
      this.store.select(UserState.isGlobalAdmin),
      this.store.select(UserState.hasOnlyPermissionsInAllCourtSystems).pipe(map(fn => fn(...TRANSCRIBER_PERMISSIONS))),
      this.store.select(FeatureFlagState.featureFlag('replace-realtime-wording')),
    ]).pipe(
      map(
        ([
          courtSystems,
          hasTranscriberPermission,
          isGlobalAdmin,
          hasOnlyTranscriberPermissions,
          replaceRealtimeWordingFeatureEnabled,
        ]) => {
          const defaultItems = DEFAULT_NAV_ITEMS(replaceRealtimeWordingFeatureEnabled)

          return {
            navGroups: [
              ...(hasTranscriberPermission ? [{ items: [defaultItems['transcript-jobs']] }] : []),
              {
                title: 'My content',
                items: [defaultItems['upload-and-play'], defaultItems['my-orders'], defaultItems['shared-recordings']],
              },
              {
                title: 'Place an order',
                items: [defaultItems['order-audio'], defaultItems['order-real-time'], defaultItems['order-transcript']],
              },
            ],
            footerItems: [
              ...(isGlobalAdmin ? [getGlobalAdminNavItem()] : []),
              ...(courtSystems?.length > 0 && !hasOnlyTranscriberPermissions
                ? [defaultItems['select-court-system']]
                : []),
              getHelpSupportNavItem(this.appDownloadService.getStandaloneAppLink()),
            ],
          }
        },
      ),
    )
  }
}
